import { useState, useCallback, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { LocateFixed, Share2 } from 'lucide-react';
import { setShowShareCalendar } from '../../Redux/store';
import Calendar from './Calendar';
import Tests from './Tests';
import OvulationSchedule from './OvulationSchedule';
import CalendarDesktop from './Desktop/CalendarDesktop';
import DesktopSwitch from '../../components/DesktopSwitch';
import Tabs from '../../components/Tabs';
import OvulationTestButton from '../../components/OvulationTestButton';
import TodayButton from '../../components/TodayButton';
import useProfileData from '../../hooks/useProfileData';
import CalendarBottomMenu from './CalendarBottomMenu';
import CalendarBottomMenu2 from './CalendarBottomMenu2';
import { getLocalTodayString } from '../../utils/dateFormat';

/**
 * Calendar page with tabs + floating action buttons (Today, Share, Test)
 * 
 * NOTE: Calendar is a controlled component - the parent manages date range state.
 * This allows different persistence strategies:
 * - CalendarPage uses Redux to persist date range across navigation
 * - ProfileFull uses local state since it's a temporary modal
 * Calendar receives startMonth/endMonth as props and calls onStartMonthChange/onEndMonthChange
 * when the user scrolls to load more months.
 */
function CalendarWithTabs({ calendarData, startMonth, endMonth, onStartMonthChange, onEndMonthChange, userId, isLoading = false, calendarId: propCalendarId }) {
  // State for which tab is active (calendar or tests)
  const [tab, setTab] = useState('calendar');
  
  // Refs for scroll containers and today indicator
  const testsRef = useRef(null);
  const scheduleRef = useRef(null);
  const todayRef = useRef(null);

  // Generate a random calendar ID for this instance, or use the one passed in
  const calendarId = useMemo(() => propCalendarId || Math.random().toString(36).substring(2, 15), [propCalendarId]);

  // Get current user ID from Redux for comparison
  const currentUserId = useSelector((s) => s.user.profile?.id);
  // Get current user role
  const currentRole = useSelector((s) => s.user.profile?.role);
  // Check if viewing own calendar or another user's calendar
  const isOwnCalendar = !userId || userId === currentUserId;
  const dispatch = useDispatch();

  // Fetch viewed user's profile data to get their role (if viewing another user)
  const { profile: viewedProfile, loading: profileLoading } = useProfileData(userId);
  // Use viewed user's role if viewing another user, otherwise use current user's role
  const role = isOwnCalendar ? currentRole : viewedProfile?.role;
  const isIp = role === 'ip';

  // Determine tabs based on viewed user's role (not current user's role)
  // When viewing someone else's calendar, show what they would see
  // IP: calendar, tests
  // Donor: calendar, schedule (ovulation predictions from shared IPs)
  // If viewing another user and profile is still loading, show calendar only as fallback
  const tabs = (!isOwnCalendar && profileLoading)
    ? [{ name: 'calendar', disabled: false }]
    : isIp
    ? [
        { name: 'calendar', disabled: false },
        { name: 'tests', disabled: false },
      ]
    : [
        { name: 'calendar', disabled: false },
        { name: 'schedule', disabled: false },
      ];

  // Show test button only for IP on own calendar
  const showTestButton = isIp && isOwnCalendar;

  // Scroll to today's date in calendar or top of tests/schedule list
  const scrollToToday = useCallback((autoDelay = null, targetCalendarId = null) => {
    const doScroll = () => {
      if (tab === 'calendar') {
        // Scroll to today's date in calendar using data-date and data-calendar attributes
        const todayStr = getLocalTodayString();
        const selector = targetCalendarId
          ? `[data-date="${todayStr}"][data-calendar="${targetCalendarId}"]`
          : `[data-date="${todayStr}"]`;
        const el = document.querySelector(selector);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else if (tab === 'tests') {
        // Scroll to top of tests list using ref
        if (testsRef.current) {
          testsRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } else if (tab === 'schedule') {
        // Scroll to today divider using ref
        if (todayRef.current) {
          todayRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else if (scheduleRef.current) {
          // Fallback to top if no today ref
          scheduleRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    };

    if (autoDelay !== null) {
      setTimeout(doScroll, autoDelay);
    } else {
      doScroll();
    }
  }, [tab]);

  // Shared props for both calendar views
  const calendarProps = {
    calendarData,
    startMonth,
    endMonth,
    onStartMonthChange,
    onEndMonthChange,
    userId: userId || currentUserId,
    isLoading,
    calendarId,
  };

  return (
    <div className="flex h-full flex-col relative w-full">
      {/* Tab selector for switching between calendar and tests/schedule views */}
      <Tabs tabs={tabs} selectedTab={tab} onTabSet={setTab} />

      {/* Content area with fixed height */}
      <div className="flex-1 overflow-hidden relative">
        {/* Calendar view - shows monthly calendar with ovulation data */}
        {tab === 'calendar' && (
          <DesktopSwitch desktop={CalendarDesktop} mobile={Calendar} desktopProps={calendarProps} mobileProps={calendarProps} />
        )}

        {/* Tests view - shows list of ovulation tests (IP only) */}
        {tab === 'tests' && (
          <div ref={testsRef} className="h-full overflow-y-auto">
            <Tests userId={userId || currentUserId} calendarData={calendarData} />
          </div>
        )}

        {/* Schedule view - shows upcoming ovulation predictions from shared IPs (donor only) */}
        {tab === 'schedule' && (
          <div ref={scheduleRef} className="h-full overflow-y-auto">
            <OvulationSchedule calendarData={calendarData} todayRef={todayRef}/>
          </div>
        )}

        {/* Original buttons - commented out in case we need to restore */}
        {/* Today button — bottom-right, higher when viewing other user's calendar */}
        {/* {tab === 'calendar' && (
          <TodayButton calendarId={calendarId} onClick={scrollToToday} autoScrollDelay={250} />
        )} */}

        {/* Share button — bottom-left, IP own calendar only */}
        {/* {isIp && isOwnCalendar && tab === 'calendar' && (
          <button
            onClick={() => dispatch(setShowShareCalendar(true))}
            className="absolute bottom-2.5 left-4 z-20 flex items-center gap-1.5 rounded-full border border-border bg-purple-500/20 px-3 py-2 text-xs font-semibold text-foreground shadow-lg backdrop-blur-sm hover:bg-purple-500/30 active:scale-95 transition"
          >
            <Share2 className="h-3.5 w-3.5" />
            Share
          </button>
        )} */}

        {/* Ovulation test camera button — center bottom, IP own calendar, calendar + tests tabs */}
        {/* {showTestButton && (tab === 'calendar' || tab === 'tests') && (
          <OvulationTestButton />
        )} */}

        {/* Calendar bottom menu with raised bump containing buttons */}
        {/* <CalendarBottomMenu
          calendarId={calendarId}
          scrollToToday={scrollToToday}
          showTestButton={showTestButton && (tab === 'calendar' || tab === 'tests')}
          showShareButton={isIp && isOwnCalendar && tab === 'calendar'}
          showTodayButton={tab === 'calendar'}
        /> */}

        {/* Calendar bottom menu 2 - white overlay */}
        <CalendarBottomMenu2
          calendarId={calendarId}
          scrollToToday={scrollToToday}
          isIp={isIp}
          isOwnCalendar={isOwnCalendar}
        />
      </div>
    </div>
  );
}

export default CalendarWithTabs;
